import React, { useCallback, useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import ReviewActionBlock from "@/components/ReviewActionBlock";
import { adminApi } from "@/lib/api";

// Pass 53 — dedicated Reviews tab. Previously this was a compact
// teaser list (`ReviewQueue`) embedded in the Dashboard that only
// linked into the Leads drawer to actually act — this page IS the
// action surface: every pending item gets its own inline
// ReviewActionBlock (personal line + Send WhatsApp / Send Email /
// Skip), no click-through required.
//
// `/api/admin/reviews` returns a compact, list-shaped payload
// (_serialize_review_item in backend/server.py) — different field
// names from a full lead row. ReviewActionBlock expects a lead-shaped
// object (`.ID`, `.Customer_Name`, `.Customer_Email`, `.has_phone`,
// `.has_email`, `.review_link`, `.review_pending`), so `toLeadLike()`
// below adapts one to the other rather than fetching each full lead
// (would be N extra requests for no benefit — the compact payload
// already has everything the action block needs).

function formatWonDate(s) {
  const d = (s || "").split(" ")[0];
  if (!d) return "—";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-MY", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return d; }
}

function toLeadLike(item, done) {
  return {
    ID: item.lead_id,
    Customer_Name: item.customer_name,
    Customer_Email: item.customer_email,
    // Flipping this after an action is what makes ReviewActionBlock
    // render its OWN success banner (its designed !pending behaviour).
    // Refetching the list instead would unmount the card mid-action and
    // the operator would never see the confirmation — on a page whose
    // entire job is sending, the send has to visibly confirm.
    //
    // A Review_Status:"sent" companion used to be needed here to work
    // around the `||` in that component's pending predicate. Pass 55
    // fixed the predicate at source, so review_pending alone is now
    // authoritative and the companion field is gone.
    review_pending: !done,
    has_phone: (item.channels || []).includes("whatsapp"),
    has_email: (item.channels || []).includes("email"),
    review_link: item.review_link,
  };
}

export default function AdminReviews() {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  // lead_ids actioned in THIS page session. Kept client-side so the
  // handled card stays put with its confirmation instead of vanishing;
  // it clears on the next load of the page, by which point the server
  // no longer returns it as pending anyway.
  const [done, setDone] = useState(() => new Set());

  const load = useCallback(async () => {
    try {
      const r = await adminApi.reviews();
      setItems(r.data?.items || []);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load review requests.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markDone = useCallback((leadId) => {
    setDone((prev) => new Set(prev).add(leadId));
  }, []);

  return (
    <AdminLayout>
      <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Post-sale</p>
      <h1 style={{ fontSize: 28, margin: "0 0 8px 0" }}>Reviews</h1>
      <p style={{ fontSize: 13, color: "var(--scw-muted)", marginBottom: 28, maxWidth: 640, lineHeight: 1.6 }}>
        Closed-won deals become eligible 3 days after the win and stay
        eligible for 14 days. Each card sends from the salesperson's own
        identity — WhatsApp opens their personal wa.me link, email carries
        their name and Reply-To.
      </p>

      {err && <p style={{ color: "#a13b3b", fontSize: 13, marginBottom: 16 }} data-testid="admin-reviews-error">{err}</p>}

      {items === null ? (
        <p style={{ color: "var(--scw-muted)" }}>{"Loading…"}</p>
      ) : items.length === 0 ? (
        <div className="scw-card" data-testid="admin-reviews-empty">
          <p style={{ margin: 0, color: "var(--scw-muted)" }}>No pending review requests.</p>
        </div>
      ) : (
        <div data-testid="admin-reviews-list">
          {items.map((it) => (
            <div key={it.lead_id} className="scw-card" style={{ marginBottom: 16 }} data-testid={`admin-reviews-card-${it.lead_id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 4, flexWrap: "wrap" }}>
                <div>
                  <p style={{ margin: 0, fontSize: 17, fontWeight: 500 }}>{it.customer_name}</p>
                  <p style={{ margin: "2px 0 0 0", fontSize: 13, color: "var(--scw-muted)" }}>
                    {it.watch}{it.store === "JB" ? " · JB" : ""}
                  </p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <span className="scw-eyebrow">Won {formatWonDate(it.won_date)}</span>
                  <p style={{ margin: "4px 0 0 0", fontSize: 12, color: "var(--scw-muted)" }}>{it.salesperson}</p>
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <ReviewActionBlock
                  lead={toLeadLike(it, done.has(it.lead_id))}
                  apiClient={adminApi}
                  onActionComplete={() => markDone(it.lead_id)}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
